import React, { useState, useEffect } from 'react';

import { Separator } from '@cuemath/comps';

import {
  COURSE,
  DEFAULT_CLASS_RATIO,
  VALIDITY_MODE,
  VIEW_MODE,
  CHANNEL,
} from './constants';
import { useRegionPayoutData } from './use-region-payout-data';
import { useBulkCreate } from './use-bulk-create';
import { useRegionEditEnd } from './use-region-edit-end';
import Tabs from './components/tabs';
import PayoutControls from './components/payout-controls';
import PayoutBody from './components/payout-body';
import { Root, Header, Title, Subtitle } from './styled-comps';

const MODE_TABS = [
  { key: VIEW_MODE.GRID, label: 'View rates' },
  { key: VIEW_MODE.BULK, label: 'Bulk create' },
];

const RegionPayout = () => {
  const [mode, setMode] = useState(VIEW_MODE.GRID);
  const [course, setCourse] = useState(COURSE);
  const [activeRegion, setActiveRegion] = useState('');
  const [channel, setChannel] = useState(CHANNEL.ONLINE);
  const [classRatio, setClassRatio] = useState(DEFAULT_CLASS_RATIO);
  const [validityMode, setValidityMode] = useState(VALIDITY_MODE.ACTIVE);
  const [asOfDate, setAsOfDate] = useState('');

  const {
    rows,
    isProcessing,
    fetchPayouts,
    courses,
    visibleRegions,
    ratioOptions,
    cellData,
    windowsForCell,
    visibleGradeRanges,
    visibleTiers,
  } = useRegionPayoutData({
    mode,
    course,
    classRatio,
    activeRegion,
    channel,
    validityMode,
    asOfDate,
  });

  const {
    bulkValues,
    bulkErrors,
    bulkStart,
    bulkEnd,
    bulkDateError,
    bulkSubmitting,
    resetBulk,
    setBulkValue,
    onBulkStart,
    onBulkEnd,
    submitBulk,
  } = useBulkCreate({
    activeRegion,
    course,
    classRatio,
    cellData,
    windowsForCell,
    visibleGradeRanges,
    visibleTiers,
    fetchPayouts,
  });

  const { openRegionEditEnd } = useRegionEditEnd({
    activeRegion,
    classRatio,
    cellData,
    visibleGradeRanges,
    visibleTiers,
    fetchPayouts,
  });

  // Keep the selected course valid for the current dataset.
  useEffect(() => {
    if (courses.length && !courses.includes(course)) setCourse(courses[0]);
  }, [courses, course]);

  // Keep the active region tab valid for the current channel + dataset.
  useEffect(() => {
    if (!visibleRegions.length) return;
    if (!visibleRegions.includes(activeRegion)) setActiveRegion(visibleRegions[0]);
  }, [visibleRegions, activeRegion]);

  // Keep the selected ratio valid for the current dataset.
  useEffect(() => {
    if (!ratioOptions.length) return;
    if (!ratioOptions.some(o => o.value === classRatio)) {
      setClassRatio(ratioOptions[0].value);
    }
  }, [ratioOptions, classRatio]);

  // Changing any filter clears in-progress bulk edits before applying the new selection.
  const selectWith = setter => value => {
    resetBulk();
    setter(value);
  };

  const isBulk = mode === VIEW_MODE.BULK;

  return (
    <Root>
      <Header>
        <Title>Region Payout</Title>
        <Subtitle>
          Teacher per-class rates · {course}. Each rate is effective-dated; edits are
          forward-only and existing fees keep their snapshot.
        </Subtitle>
      </Header>

      {courses.length > 1 && (
        <Tabs
          items={courses.map(c => ({ key: c, label: c }))}
          activeKey={course}
          onSelect={selectWith(setCourse)}
        />
      )}

      <Tabs items={MODE_TABS} activeKey={mode} onSelect={selectWith(setMode)} />

      <PayoutControls
        isBulk={isBulk}
        channel={channel}
        onChannel={selectWith(setChannel)}
        classRatio={classRatio}
        onClassRatio={selectWith(setClassRatio)}
        ratioOptions={ratioOptions}
        validityMode={validityMode}
        onValidityMode={setValidityMode}
        asOfDate={asOfDate}
        onAsOfDate={setAsOfDate}
        bulkStart={bulkStart}
        onBulkStart={onBulkStart}
        bulkEnd={bulkEnd}
        onBulkEnd={onBulkEnd}
        bulkSubmitting={bulkSubmitting}
      />

      {isBulk && (
        <Subtitle>
          Sets one new effective-dated window per cell using the common dates above.
          Amounts are prefilled with the current rate — edit or clear a cell to skip it. A
          window that overlaps an existing one is flagged on that cell.
        </Subtitle>
      )}

      <Tabs
        items={visibleRegions.map(r => ({ key: r, label: r }))}
        activeKey={activeRegion}
        onSelect={selectWith(setActiveRegion)}
      />

      <PayoutBody
        isBulk={isBulk}
        loading={isProcessing && !rows.length}
        activeRegion={activeRegion}
        visibleGradeRanges={visibleGradeRanges}
        visibleTiers={visibleTiers}
        cellData={cellData}
        bulkValues={bulkValues}
        bulkErrors={bulkErrors}
        bulkDateError={bulkDateError}
        bulkSubmitting={bulkSubmitting}
        onBulkChange={setBulkValue}
        onEditEnd={openRegionEditEnd}
        onSubmitBulk={submitBulk}
      />

      <Separator height={8} transparent />
    </Root>
  );
};

export default RegionPayout;
